import { useEffect, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import isEmpty from 'lodash-es/isEmpty';

import useTheme from '@rover/kibble/themes/useTheme';
import { Geometry, ValidGeometryTypes } from '@rover/react-lib/src/components/GeoMap/types';
import { getZoom } from '@rover/react-lib/src/pages/search/SearchPage/utilities';

export interface GeoMapPolygonProps {
  geometry: Geometry;
  strokeWeight?: number;
  fillColor?: string;
  fillOpacity?: number;
}

/**
 * GeoMapPolygon component renders GeoJSON geometry on the map using map.data layer.
 * Supports all GeoJSON geometry types including Polygon, MultiPolygon, LineString, etc.
 */
const GeoMapPolygon = ({
  geometry,
  strokeWeight = 0,
  fillColor,
  fillOpacity = 0.5,
}: GeoMapPolygonProps): null => {
  const [area, setArea] = useState<google.maps.Data.Feature | null>(null);

  const map = useMap();
  const { colors } = useTheme();
  const resolvedFillColor = fillColor || colors.blue['700'];

  // create marker and polygon
  useEffect(() => {
    if (map) {
      if (!isEmpty(geometry) && geometry?.type && ValidGeometryTypes.has(geometry.type as string)) {
        const geoJson = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry,
            },
          ],
        };

        if (area) {
          map.data.remove(area);
        }
        setArea(map.data.addGeoJson(geoJson)[0]);
        map.data.setStyle({
          fillColor: resolvedFillColor,
          fillOpacity,
          strokeWeight,
        });
      }
    }
  }, [map, geometry, resolvedFillColor, fillOpacity, strokeWeight]);

  useEffect(() => {
    if (map) {
      const bounds = new google.maps.LatLngBounds();
      map.data.forEach((feature: google.maps.Data.Feature) => {
        feature.getGeometry()!.forEachLatLng((latLng: google.maps.LatLng) => {
          bounds.extend(latLng);
        });
      });

      // get necessary zoom level to fit polygon bounds
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();
      const zoom = getZoom(northEast.lat(), northEast.lng(), southWest.lat(), southWest.lng());

      // set zoom
      map.setZoom(Math.round(zoom));
    }
  }, [map, geometry]);

  return null;
};

export default GeoMapPolygon;
